import { makeObservable, observable, action, computed } from "mobx";
import { RequestState } from "../constants";
import { userStore } from "../stores";
import { apiService } from "../services";

export class UserPreferencesController {
  preferences = {
    // Food preferences
    favoriteFoods: [],
    allergies: [],

    // Health and fitness goals
    dietaryGoals: [],
    currentWeight: "",
    targetWeight: "",
    height: "",
    activityLevel: 1.375, // Lightly active by default

    // Daily nutrition targets
    dailyCalories: 2000,
    dailyProtein: 150,
    dailyCarbs: 200,
    dailyFat: 65,

    // Dietary restrictions
    isVegetarian: false,
    isVegan: false,
    isKeto: false,
    isPaleo: false,
    isGlutenFree: false,
    isDairyFree: false,
  };

  saveRequestState = RequestState.IDLE;
  saveMessage = "";
  loadRequestState = RequestState.IDLE;

  constructor() {
    makeObservable(this, {
      preferences: observable,
      saveRequestState: observable,
      saveMessage: observable,
      loadRequestState: observable,
      updatePreference: action,
      savePreferences: action,
      loadUserPreferences: action,
      resetPreferences: action,
      setSaveRequestState: action,
      setSaveMessage: action,
      setLoadRequestState: action,
      getSaveRequestState: computed,
      getSaveMessage: computed,
      getLoadRequestState: computed,
    });
  }

  get getSaveRequestState() {
    return this.saveRequestState;
  }

  get getSaveMessage() {
    return this.saveMessage;
  }

  get getLoadRequestState() {
    return this.loadRequestState;
  }

  setSaveRequestState(state) {
    this.saveRequestState = state;
  }

  setSaveMessage(message) {
    this.saveMessage = message;
  }

  setLoadRequestState(state) {
    this.loadRequestState = state;
  }

  updatePreference(key, value) {
    // Handle nested properties (like preferences.dailyCalories)
    if (key.includes(".")) {
      const keys = key.split(".");
      let current = this.preferences;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
    } else {
      this.preferences[key] = value;
    }
  }

  async loadUserPreferences() {
    if (!userStore.isLoggedIn) {
      return;
    }

    try {
      this.setLoadRequestState(RequestState.REQUESTED);

      const response = await apiService.get("/user/get_preferences");

      if (response.data && response.data.preferences) {
        // Merge server preferences with defaults
        this.preferences = {
          ...this.preferences,
          ...response.data.preferences,
        };
      }

      this.setLoadRequestState(RequestState.SUCCESS);
    } catch (error) {
      console.error("Error loading user preferences:", error);
      this.setLoadRequestState(RequestState.ERROR);
    }
  }

  async savePreferences() {
    if (!userStore.isLoggedIn) {
      this.setSaveMessage("Please log in to save preferences");
      return;
    }

    try {
      this.setSaveRequestState(RequestState.REQUESTED);
      this.setSaveMessage("");

      // Prepare preferences data for backend
      const preferencesData = {
        // Convert arrays to comma-separated strings for backend compatibility
        favorite_foods: this.preferences.favoriteFoods.join(","),
        allergies: this.preferences.allergies.join(","),
        dietary_goals: this.preferences.dietaryGoals.join(","),

        // Physical information
        weight: this.preferences.currentWeight
          ? parseInt(this.preferences.currentWeight)
          : null,
        target_weight: this.preferences.targetWeight
          ? parseInt(this.preferences.targetWeight)
          : null,
        height: this.preferences.height,
        activity_level: this.preferences.activityLevel,

        // Daily targets
        daily_calories: this.preferences.dailyCalories,
        daily_protein: this.preferences.dailyProtein,
        daily_carbs: this.preferences.dailyCarbs,
        daily_fat: this.preferences.dailyFat,

        // Dietary restrictions
        is_vegetarian: this.preferences.isVegetarian,
        is_vegan: this.preferences.isVegan,
        is_keto: this.preferences.isKeto,
        is_paleo: this.preferences.isPaleo,
        is_gluten_free: this.preferences.isGlutenFree,
        is_dairy_free: this.preferences.isDairyFree,
      };

      const response = await apiService.put("/user/update", preferencesData);

      if (response.status === 200) {
        this.setSaveRequestState(RequestState.SUCCESS);
        this.setSaveMessage("Preferences saved successfully!");

        // Update user store if needed
        if (userStore.userObj) {
          userStore.userObj.favorite_foods = preferencesData.favorite_foods;
          userStore.userObj.allergies = preferencesData.allergies;
          userStore.userObj.weight = preferencesData.weight;
        }
      }
    } catch (error) {
      console.error("Error saving preferences:", error);
      this.setSaveRequestState(RequestState.ERROR);
      this.setSaveMessage(
        error.response?.data?.error ||
          "Error saving preferences. Please try again."
      );
    }
  }

  resetPreferences() {
    this.preferences = {
      favoriteFoods: [],
      allergies: [],
      dietaryGoals: [],
      currentWeight: "",
      targetWeight: "",
      height: "",
      activityLevel: 1.375,
      dailyCalories: 2000,
      dailyProtein: 150,
      dailyCarbs: 200,
      dailyFat: 65,
      isVegetarian: false,
      isVegan: false,
      isKeto: false,
      isPaleo: false,
      isGlutenFree: false,
      isDairyFree: false,
    };
    this.setSaveMessage("");
  }

  // Helper method to calculate BMR (Basal Metabolic Rate)
  calculateBMR() {
    if (
      !this.preferences.currentWeight ||
      !this.preferences.height ||
      !userStore.userObj?.gender
    ) {
      return null;
    }

    const weight = parseFloat(this.preferences.currentWeight) * 0.453592; // Convert lbs to kg
    const height = parseFloat(this.preferences.height) * 2.54; // Convert inches to cm
    const age = userStore.userObj?.dob
      ? new Date().getFullYear() - new Date(userStore.userObj.dob).getFullYear()
      : 30;

    let bmr;
    if (userStore.userObj.gender === "male") {
      bmr = 88.362 + 13.397 * weight + 4.799 * height - 5.677 * age;
    } else {
      bmr = 447.593 + 9.247 * weight + 3.098 * height - 4.33 * age;
    }

    return Math.round(bmr * this.preferences.activityLevel);
  }

  // Method to auto-calculate daily calories based on BMR and goals
  autoCalculateCalories() {
    const bmr = this.calculateBMR();
    if (!bmr) return;

    let calorieAdjustment = 0;

    if (this.preferences.dietaryGoals.includes("Weight Loss")) {
      calorieAdjustment = -500; // 1 lb per week
    } else if (this.preferences.dietaryGoals.includes("Weight Gain")) {
      calorieAdjustment = 500; // 1 lb per week
    } else if (this.preferences.dietaryGoals.includes("Muscle Building")) {
      calorieAdjustment = 300; // Slight surplus for muscle building
    }

    const targetCalories = Math.max(1200, bmr + calorieAdjustment);
    this.updatePreference("dailyCalories", targetCalories);
  }
}
